import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { SearchIcon } from 'tdesign-icons-react';
import {
  Button,
  Col,
  DialogPlugin,
  Dropdown,
  Form,
  Input,
  Link,
  Loading,
  MessagePlugin,
  Row,
  Space,
  Tag,
} from 'tdesign-react';
import { isEmpty } from 'lodash';
import { deleteGeo } from '@/services/clusterService';
import { isVoid } from '@/utils/common';
import { fetchGeoList } from '@/slices/clusterSlice';
import SvgIcon from '@/components/SvgIcon';
import NoData from '@/components/NoData';
import PermWrapper from '@/components/PermWrapper';
import useDisableButton from '@/components/DisableWrapper/useDisableButton';
import ModalForm from './ModalForm';

function GeoPanel({ fetchGeoList, list, loading }) {
  const [searchParams] = useSearchParams();
  const [remoteClusterName, setRemoteClusterName] = useState();
  const [visible, setVisible] = useState(false);
  const [form] = Form.useForm();

  const clusterName = searchParams.get('name');
  const filter = Form.useWatch('filter', form);

  const confirmBtn = useDisableButton({
    content: '确定',
  });

  useEffect(() => {
    fetchGeoList(clusterName);
  }, [fetchGeoList, clusterName]);

  const onSelect = (value, item) => {
    switch (value) {
      case 'modify': // 修改
        setVisible(true);
        setRemoteClusterName(item);
        break;

      case 'delete': // 删除
        {
          const dialog = DialogPlugin.confirm({
            header: '提示',
            theme: 'warning',
            body: (
              <>
                确定删除？<Tag>{item}</Tag>
              </>
            ),
            confirmBtn,
            onClose: () => dialog.hide(),
            onConfirm: async () => {
              try {
                dialog.update({
                  confirmBtn: { loading: true, content: '确定' },
                });
                await deleteGeo({
                  clusterName,
                  replicationClusterName: item,
                });
                MessagePlugin.success('操作成功！');
                dialog.hide();
                fetchGeoList(clusterName);
              } catch (error) {
                console.trace(error);
                throw error;
              } finally {
                dialog.update({
                  confirmBtn: { loading: false, content: '确定' },
                });
              }
            },
          });
        }
        break;

      default:
        break;
    }
  };

  const handleClose = (refresh) => {
    setVisible(false);
    setRemoteClusterName(null);
    if (refresh) {
      fetchGeoList(clusterName);
    }
  };

  const filterList = list.filter((item) => (isVoid(filter) ? true : item.indexOf(filter) > -1));

  return (
    <Loading loading={loading} size='small'>
      <Root className='narrow-scrollbar'>
        <Bar>
          <Form form={form} layout='inline'>
            <Form.FormItem name='filter'>
              <Input style={{ width: 260 }} placeholder='请输入GEO集群名' suffix={<SearchIcon />} />
            </Form.FormItem>
          </Form>
          <Space>
            <PermWrapper resource='pulsar:cluster:GEOCreate'>
              <Button onClick={() => setVisible(true)}>添加GEO</Button>
            </PermWrapper>
          </Space>
        </Bar>
        {isEmpty(filterList) ? (
          <NoData imgStyle={{ height: '100%', marginTop: -30 }} />
        ) : (
          <Row gutter={[12, 12]}>
            {filterList.map((item) => (
              <Col span={4} key={item}>
                <Card>
                  <Image src={require('./插图.png')} />
                  <Title title={item}>{item}</Title>
                  <PermWrapper resource='pulsar:cluster:GEOConfig'>
                    <Dropdown
                      options={[
                        {
                          value: 'modify',
                          content: '修改',
                        },
                        {
                          value: 'delete',
                          content: (
                            <Link theme='danger' hover='color'>
                              删除
                            </Link>
                          ),
                        },
                      ]}
                      onClick={({ value }) => onSelect(value, item)}
                    >
                      <span>
                        <Icon type='dots-vertical' />
                      </span>
                    </Dropdown>
                  </PermWrapper>
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </Root>
      <ModalForm
        remoteClusterName={remoteClusterName}
        visible={visible}
        handleClose={handleClose}
      />
    </Loading>
  );
}

export default connect(
  (state) => ({
    list: state.cluster.geoList,
    loading: state.cluster.fetchingGeoList,
  }),
  {
    fetchGeoList,
  },
)(GeoPanel);

const Root = styled.div`
  max-height: 300px;
  overflow-y: auto;
  overflow-x: hidden;
`;

const Card = styled.div`
  border-radius: 6px;
  border: 1px solid var(--grey-line-input, rgba(0, 0, 0, 0.15));
  height: 104px;
  box-sizing: border-box;
  padding: 20px 24px;
  display: flex;
  align-items: center;
`;

const Bar = styled.div`
  display: flex;
  align-items: center;
  padding: 20px 0;
  justify-content: space-between;
`;

const Image = styled.img`
  width: 64px;
  height: 64px;
`;

const Title = styled.div`
  margin: 0 16px;
  flex: 1;
  font-size: 16px;
  color: var(--td-text-color-primary);
  font-weight: bold;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-all;
`;

const Icon = styled(SvgIcon)`
  cursor: pointer;
  font-size: 20px;
  color: var(--td-text-color-secondary);
  :hover {
    color: #194dff;
  }
`;
